import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Params } from '@angular/router';
import { Action, select, Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, filter, map, pluck, switchMap, withLatestFrom } from 'rxjs/operators';
import { merge, Observable, of } from 'rxjs';

import * as fromRouter from '~core/store';
import { ErrorResponse } from '~shared/models';
import { PriceTypeService } from '~shared/modules/price-type-toggler/price-type-toggler.module';
import * as fromCatalog from '../selectors';
import { CatalogService } from '../../services/catalog.service';
import { DEFAULT_HUB_LIMIT, DEFAULT_LIMIT } from '~app/modules/generic/constants';
import { LoadProducts, LoadProductsFailure, LoadProductsSuccess, ProductsActionTypes } from './products.actions';
import { SearchResponse } from '~app/modules/catalog/models';
import { SortOption } from '~shared/modules/application-event/models/sort-option.model';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { CustomPlpService } from '~app/modules/catalog/services/custom-plp.service';
import { CustomPlpActionTypes } from '~app/modules/catalog/store/custom-plp/custom-plp.actions';
import { RegionSelectorService } from '~shared/modules/region-selector/services';

@Injectable({
  providedIn: 'root',
})
export class ProductsEffects {
  priceType$ = this.priceTypeSvc.priceType$;
  activeRoute$ = this.store.pipe(select(fromRouter.getRouterState));
  activeSort$ = this.store.pipe(select(fromCatalog.getSortOption));
  lastFilterParams$ = this.store.pipe(select(fromCatalog.getLastRequestFilterParameters));
  userRegion$ = this.regionSelectorService.userRegion$.pipe(filter((value) => value !== undefined));

  @Effect()
  loadProducts$: Observable<Action> = this.actions$.pipe(
    ofType(ProductsActionTypes.LOAD_PRODUCTS),
    pluck('payload', 'queryOptions'),
    switchMap((queryOptions) =>
      this.catalogService.getProducts(queryOptions).pipe(
        map((response: SearchResponse) => new LoadProductsSuccess({ response, queryOptions })),
        catchError((error: ErrorResponse) => of(new LoadProductsFailure({ error })))
      )
    )
  );

  @Effect()
  updateItem$: Observable<Action> = merge(
    this.actions$.pipe(ofType(ROUTER_NAVIGATED)),
    this.priceType$.pipe(distinctUntilChanged()),
    this.activeSort$.pipe(distinctUntilChanged()),
    this.userRegion$
  ).pipe(
    withLatestFrom(this.activeRoute$, this.activeSort$.pipe(distinctUntilChanged()), this.userRegion$),
    filter(([routeOrPriceTypeOrSort, routeState, sortOption]): boolean => {
      if (!routeState?.url) {
        return false;
      }

      const isProductListPage = this.productsListUrlService.isProductListPage();
      const isCustomPLP = this.productsListUrlService.isCustomPLPPage();

      if (isCustomPLP || !isProductListPage) {
        return false;
      }

      return this.isValidSort(sortOption, routeState.queryParams, routeOrPriceTypeOrSort as SortOption);
    }),
    this.mapProductsConfiguration(),
    this.populateLoadProductsConfiguration(),
    switchMap(this.loadProducts)
  );

  @Effect()
  loadCustomPLPProducts$ = merge(
    this.actions$.pipe(ofType(ROUTER_NAVIGATED)),
    this.actions$.pipe(ofType(CustomPlpActionTypes.LOAD_CUSTOMPLP_CONFIG_CONTENT_SUCCESS)),
    this.priceType$.pipe(distinctUntilChanged()),
    this.activeSort$.pipe(distinctUntilChanged()),
    this.userRegion$.pipe(distinctUntilChanged())
  ).pipe(
    withLatestFrom(this.activeRoute$, this.activeSort$.pipe(distinctUntilChanged())),
    filter(([routeOrPriceTypeOrSort, routeState, sortOption]) => {
      if (!(routeState?.url && Object.keys(routeState?.queryParams).length !== 0)) {
        return false;
      }

      if (!this.productsListUrlService.isCustomPLPPage()) {
        return false;
      }

      return this.isValidSort(sortOption, routeState.queryParams, routeOrPriceTypeOrSort as SortOption);
    }),
    this.mapProductsConfiguration(),
    this.populateLoadProductsConfiguration(),
    switchMap(this.loadProducts)
  );

  constructor(
    private actions$: Actions,
    private catalogService: CatalogService,
    private priceTypeSvc: PriceTypeService,
    private productsListUrlService: ProductsListUrlService,
    private store: Store<fromRouter.RouterState>,
    private customPlpService: CustomPlpService,
    private regionSelectorService: RegionSelectorService
  ) {}

  populateLoadProductsConfiguration() {
    return withLatestFrom(
      this.lastFilterParams$,
      this.priceType$,
      this.userRegion$,
      ([router, sortOption], curFilter, priceType, userRegion) => {
        const { queryParams } = router;
        const { filter: queryFilter, ...restParams } = queryParams;
        const limit = DEFAULT_LIMIT;

        //Setting orderIds for custom PLP API
        let orderedIds;
        if (this.productsListUrlService.isCustomPLPPage()) {
          orderedIds = this.customPlpService.getSortedIds;
        }

        return {
          queryParams: {
            ...restParams,
            filter: {
              ...queryFilter,
              priceType,
            },
            orderBy: queryParams.orderBy || sortOption.key,
            order: queryParams.order || sortOption.value,
            limit: queryParams.limit || limit,
            orderedIds,
            ...{ region: userRegion },
          },
          curFilter,
        };
      }
    );
  }

  loadProducts({ queryParams }) {
    const params = {
      ...queryParams,
    };

    if (!('offset' in params)) {
      params.offset = 0;
    }

    return of(
      new LoadProducts({
        queryOptions: params,
      })
    );
  }

  private mapProductsConfiguration() {
    return map(([_, { url, queryParams }, sortOption, categories, isHubPagesEnabled]) => {
      const categorySlug = this.productsListUrlService.getCategory();
      let category;

      if (categorySlug && isHubPagesEnabled) {
        category = categories.find((c) => c?.slug === categorySlug);
      }

      const limit = category?.children?.length ? DEFAULT_HUB_LIMIT : queryParams.limit;
      queryParams = this.setCategoryOrSearchTerm(queryParams, categorySlug, limit);

      return [{ url, queryParams }, sortOption];
    });
  }

  private setCategoryOrSearchTerm(queryParams: Params, categorySlug: string, limit: number): Params {
    let params;
    if (this.productsListUrlService.isCategoryPage()) {
      params = {
        ...queryParams,
        limit,
        filter: {
          categories: [categorySlug],
          ...queryParams.filter,
        },
      };
    } else if (this.productsListUrlService.isSearchPage()) {
      params = {
        ...queryParams,
        filter: {
          top: { phrase: this.productsListUrlService.getSearchTerm() },
          ...queryParams.filter,
        },
      };
    } else {
      params = queryParams;
    }

    return params;
  }

  private isValidSort(currentSortOption: SortOption, queryParams: Params, sortOptionChange: SortOption): boolean {
    // don't get products unless the sort option is there ........ (wait for the default one to be set)
    if (!currentSortOption) {
      return false;
    }

    const validQueryParamOrderBy = queryParams && queryParams.orderBy && queryParams.orderBy !== 'custom';
    const isSortChange = sortOptionChange?.code && sortOptionChange.key && sortOptionChange.value;
    if (isSortChange && !validQueryParamOrderBy) {
      return false;
    }

    if (
      validQueryParamOrderBy &&
      (queryParams.orderBy !== currentSortOption.key ||
        (queryParams.orderBy === currentSortOption.key && queryParams.order !== currentSortOption.value))
    ) {
      return false;
    }

    return true;
  }
}
